import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { createLabel, LabelType } from 'common/components/InsuranceSelect/InsuranceSelect';
import { COUNTRY } from 'common/constants';
import { selectDataClinicCountry, selectInsuranceListForCountry } from 'connect/selectors';
import { InputType } from 'pages/checkout/components/common/Input';
import { CheckoutInsurancePlan, CheckoutInsuranceProvider } from 'pages/checkout/connect/types';

import { AutocompleteOption } from 'pages/common/components/Autocomplete/types';

import { FormInputConfig, FormInputs } from '../renderer-helpers';

interface InsurancePickerProps {
  selectedInsuranceProvider: CheckoutInsuranceProvider | null;
  onSelectInsuranceProvider: (provider: CheckoutInsuranceProvider | null) => void;
  selectedInsurancePlan: CheckoutInsurancePlan | null;
  onSelectInsurancePlan: (plan: CheckoutInsurancePlan | null) => void;
  providerErrorMessage?: string;
  planErrorMessage?: string;
}

export const InsurancePicker = ({
  selectedInsuranceProvider,
  onSelectInsuranceProvider,
  selectedInsurancePlan,
  onSelectInsurancePlan,
  providerErrorMessage,
  planErrorMessage,
}: InsurancePickerProps) => {
  const clinicCountry = useSelector(selectDataClinicCountry);
  const isClinicUS = clinicCountry === COUNTRY.unitedStates;

  const insuranceList = useSelector<any, CheckoutInsuranceProvider[]>(selectInsuranceListForCountry);

  const allProviders = useMemo<CheckoutInsuranceProvider[]>(
    () => (insuranceList || []).sort((providerA, providerB) => providerA.name.localeCompare(providerB.name)),
    [insuranceList],
  );

  const handleSelectInsuranceProvider = useCallback((providerId: string) => {
    if (providerId !== selectedInsuranceProvider?.id) {
      onSelectInsurancePlan(null);
    }

    const fullProvider = allProviders.find((p) => p.id === providerId);

    if (fullProvider) {
      onSelectInsuranceProvider(fullProvider);
      return;
    }

    onSelectInsuranceProvider(null);
  }, [allProviders, onSelectInsuranceProvider, onSelectInsurancePlan, selectedInsuranceProvider]);

  const allProviderPlans = useMemo<CheckoutInsurancePlan[]>(() => {
    if (!selectedInsuranceProvider?.id) {
      return [];
    }

    const providerWithPlans = allProviders.find((provider) => provider.id === selectedInsuranceProvider.id);
    const allProviderPlans =
      (providerWithPlans?.plans || [])
        .sort(
          (planA, planB) => planA.name.localeCompare(planB.name),
        );

    return allProviderPlans;
  }, [allProviders, selectedInsuranceProvider?.id]);

  const handleSelectInsurancePlan = useCallback((planId: string) => {
    const fullPlan = allProviderPlans.find((p) => p.id === planId);
    if (fullPlan) {
      onSelectInsurancePlan(fullPlan);
      return;
    }

    onSelectInsurancePlan(null);
  }, [allProviderPlans, onSelectInsurancePlan]);

  const combinedProvidersList = useMemo(() => {
    const popularProviders = allProviders.filter((provider) => provider.isPopular);
    const providers = allProviders.filter((provider) => !provider.isPopular);

    const combinedList: (CheckoutInsuranceProvider | LabelType)[] = [];

    if (popularProviders?.length) {
      combinedList.push(
        createLabel('Popular Providers'),
        ...popularProviders,
      );
    }

    if (providers?.length) {
      combinedList.push(
        createLabel(
          popularProviders?.length ? 'Other Providers' : 'All Providers',
        ),
        ...providers,
      );
    }

    return combinedList.map((provider) => ({
      id: provider.id,
      name: provider.name,
      isDisabled: 'isDisabled' in provider ? provider.isDisabled : false,
      isLabel: 'isLabel' in provider ? provider.isLabel : false,
    })) as AutocompleteOption[];
  }, [allProviders]);

  const combinedPlansList = useMemo(() => {
    const popularPlans = allProviderPlans.filter((plan) => plan.isPopular);
    const plans = allProviderPlans.filter((plan) => !plan.isPopular);

    const combinedList: (CheckoutInsurancePlan | LabelType)[] = [];

    if (popularPlans?.length) {
      combinedList.push(createLabel('Popular Plans'));
      combinedList.push(...popularPlans);
    }

    if (plans?.length) {
      combinedList.push(
        createLabel(popularPlans?.length ? 'Other Plans' : 'All Plans'),
      );
      combinedList.push(...plans);
    }

    return combinedList.map((plan) => ({
      id: plan.id,
      name: plan.name,
      isDisabled: 'isDisabled' in plan ? plan.isDisabled : false,
      isLabel: 'isLabel' in plan ? plan.isLabel : false,
    })) as AutocompleteOption[];
  }, [allProviderPlans]);

  const insuranceSelectors = useMemo<FormInputConfig[]>(() => [
    {
      label: 'Insurance Carrier',
      type: InputType.AUTOCOMPLETE_DROPDOWN,
      autoSelectOnBlur: true,
      options: combinedProvidersList,
      value: selectedInsuranceProvider?.id,
      onSelect: handleSelectInsuranceProvider,
      errorMessage: providerErrorMessage,
    },
    ...isClinicUS
      ? [{
        label: 'Insurance Plan',
        type: InputType.AUTOCOMPLETE_DROPDOWN,
        autoSelectOnBlur: true,
        options: combinedPlansList,
        value: selectedInsurancePlan?.id,
        onSelect: handleSelectInsurancePlan,
        errorMessage: planErrorMessage,
      }] as FormInputConfig[]
      : [],
  ], [
    combinedProvidersList,
    combinedPlansList,
    handleSelectInsuranceProvider,
    handleSelectInsurancePlan,
    isClinicUS,
    planErrorMessage,
    providerErrorMessage,
    selectedInsurancePlan?.id,
    selectedInsuranceProvider?.id,
  ]);

  return (
    <FormInputs inputConfigs={insuranceSelectors} />
  );
};
